import type AhaEditor from '../../AhaEditor';
import {
  type TableCellElement,
  type TableElement,
} from '../../types/aha-editor';
import { getTableStructureFromElement } from './TableStructure';
import TableUtils from './TableUtils';
import {
  Editor,
  type Node,
  Path,
  Text,
  Transforms,
  Element,
  type Range as SlateRange,
  type NodeEntry,
} from 'slate';
import { ReactEditor } from 'slate-react';
import EditorUtils from '../../AhaEditorUtils/EditorUtils';
import NodeUtils from '../../AhaEditorUtils/NodeUtils';

type Direction = 'up' | 'down';

const extendSelectionToRange = (
  editor: Editor,
  range: SlateRange,
  extendSelection: boolean
) => {
  if (extendSelection) {
    Transforms.setSelection(editor, {
      focus: range.focus,
    });
  } else {
    Transforms.select(editor, range);
  }
};

const getFirstTextNode = (editor: Editor, path: Path) => {
  const [textNode] = Editor.nodes<Text>(editor, {
    at: path,
    match: (n) => Text.isText(n),
  });
  return textNode;
};

const getLastTextNode = (editor: Editor, path: Path) => {
  const [textNode] = Editor.nodes<Text>(editor, {
    at: path,
    match: (n) => Text.isText(n),
    reverse: true,
  });
  return textNode;
};

const moveCursorToText = (
  editor: Editor,
  targetNode: NodeEntry,
  targetRect: DOMRect,
  cursorRect: DOMRect,
  extendSelection: boolean
) => {
  const { document } = ReactEditor.getWindow(editor);
  // If the cursor is to the left of the table, we'll skip the table
  // entirely. Instead, clamp it to the edge of the target rect.
  const xPosition = Math.max(cursorRect.left, targetRect.left);
  let newRange: Range | null = null;

  // @ts-expect-error document.caretRangeFromPoint for WebKit
  if (document.caretRangeFromPoint) {
    newRange = window.document.caretRangeFromPoint(
      xPosition,
      targetRect.top + targetRect.height / 2
    );
  } else {
    const newPosition = (document as any).caretPositionFromPoint(
      xPosition,
      targetRect.top
    );

    if (!newPosition) {
      throw new Error('newPosition not found');
    }

    newRange = document.createRange();
    newRange.setStart(newPosition.offsetNode, newPosition.offset);
    newRange.setEnd(newPosition.offsetNode, newPosition.offset);
  }

  let selection: SlateRange | null = null;

  if (newRange) {
    selection = ReactEditor.toSlateRange(editor, newRange, {
      exactMatch: false,
      suppressThrow: true,
    });
  }

  if (!selection) {
    // Not able to get range from the browser. Fall back to the first text node.
    const firstTextNode = getFirstTextNode(editor, targetNode[1]);

    selection = {
      focus: { path: firstTextNode[1], offset: 0 },
      anchor: { path: firstTextNode[1], offset: 0 },
    };
  }

  extendSelectionToRange(editor, selection, extendSelection);
};

const targetBlockOutsideTable = (
  editor: Editor,
  direction: Direction,
  tablePath: Path
) => {
  const options = {
    match: (n: Node) => Element.isElement(n) && Editor.isBlock(editor, n),
  };

  return direction === 'down'
    ? NodeUtils.nextSibling(editor, tablePath, options)
    : NodeUtils.previousSibling(editor, tablePath, options);
};

// Find the next/previous text node relative to the current block.
const findTargetText = (editor: Editor, direction: Direction) => {
  const currentNode = Editor.above(editor, {
    match: (n) => Element.isElement(n) && Editor.isBlock(editor, n),
    mode: 'lowest',
  });
  if (!currentNode) return;

  if (direction === 'down') {
    return Editor.next(editor, {
      at: currentNode[1],
      match: (n) => Text.isText(n),
      mode: 'lowest',
    });
  }

  return Editor.previous(editor, {
    at: currentNode[1],
    match: (n) => Text.isText(n),
    mode: 'lowest',
  });
};

// Find the cell of a target table to move to, based on the position
// of the cursor.
const findTargetCell = (
  editor: Editor,
  table: NodeEntry<TableElement>,
  cursorRect: DOMRect,
  direction: Direction
) => {
  let targetCell: NodeEntry<TableCellElement> | undefined;
  const tableStructure = getTableStructureFromElement(editor, table[0]);
  if (!tableStructure) return;

  if (direction === 'down') {
    for (const row of tableStructure.rows) {
      const actualCell = row.find((r) => r.isActual);
      if (!actualCell) continue;

      const rowTargetRect = ReactEditor.toDOMNode(
        editor,
        actualCell.tableRow
      ).getBoundingClientRect();
      if (rowTargetRect.top <= cursorRect.bottom) {
        continue;
      }

      for (const cell of row) {
        if (!cell.isActual) continue;

        const cellTargetRect = ReactEditor.toDOMNode(
          editor,
          cell.tableCell
        ).getBoundingClientRect();
        if (
          cellTargetRect.left - 1 < cursorRect.right &&
          cellTargetRect.right > cursorRect.left
        ) {
          return [
            cell.tableCell,
            [...table[1], ...cell.relativePath],
          ] as NodeEntry<TableCellElement>;
        }
      }
      if (!targetCell) {
        targetCell = [
          row[row.length - 1].tableCell,
          [...table[1], ...row[row.length - 1].relativePath],
        ] as NodeEntry<TableCellElement>;
      }
    }
  } else {
    for (let y = tableStructure.height - 1; y >= 0; y--) {
      const row = tableStructure.rows[y];
      const actualCell = row.find((r) => r.isActual);
      if (!actualCell) continue;

      const rowTargetRect = ReactEditor.toDOMNode(
        editor,
        actualCell.tableRow
      ).getBoundingClientRect();
      if (rowTargetRect.bottom >= cursorRect.top) {
        continue;
      }
      for (let x = row.length - 1; x >= 0; x--) {
        const cell = row[x];
        if (!cell.isActual) continue;
        const cellTargetRect = ReactEditor.toDOMNode(
          editor,
          cell.tableCell
        ).getBoundingClientRect();
        if (
          cellTargetRect.right > cursorRect.left &&
          cellTargetRect.left - 1 < cursorRect.right
        ) {
          return [
            cell.tableCell,
            [...table[1], ...cell.relativePath],
          ] as NodeEntry<TableCellElement>;
        }
      }
      if (!targetCell) {
        targetCell = [
          row[0].tableCell,
          [...table[1], ...row[0].relativePath],
        ] as NodeEntry<TableCellElement>;
      }
    }
  }
  return targetCell;
};

const ifCellChanged = (
  editor: Editor,
  currentCell: NodeEntry<TableCellElement> | undefined,
  onChanged: () => void
) => {
  window.requestAnimationFrame(() => {
    const domSelection = window.getSelection();
    if (!domSelection?.focusNode?.parentNode) return;

    const newNode = ReactEditor.toSlateNode(
      editor,
      domSelection.focusNode.parentNode
    );

    const newCell = TableUtils.parentCell(
      editor,
      ReactEditor.findPath(editor, newNode)
    );

    if (!newCell && !currentCell) {
      return;
    }

    if (
      (!newCell && currentCell) ||
      (newCell && !currentCell) ||
      (newCell && currentCell && !Path.equals(newCell[1], currentCell[1]))
    ) {
      onChanged();
    }
  });
};

const findTargetRectInCell = (
  editor: Editor,
  targetCell: NodeEntry<TableCellElement>,
  cursorRect: DOMRect,
  direction: Direction
) => {
  const targetText =
    direction === 'down'
      ? getFirstTextNode(editor, targetCell[1])
      : getLastTextNode(editor, targetCell[1]);
  return findTargetRectInText(editor, targetText, cursorRect, direction);
};

const findTargetRectInText = (
  editor: Editor,
  targetText: NodeEntry<Text> | undefined,
  cursorRect: DOMRect,
  direction: Direction
) => {
  if (!targetText) return;
  const targetRects = Array.from(
    ReactEditor.toDOMNode(editor, targetText[0]).getClientRects()
  );

  if (direction === 'down') {
    return targetRects.reduce((targetRect, rect) => {
      if (rect.top > targetRect.top || rect.right > cursorRect.left) {
        return targetRect;
      } else {
        return rect;
      }
    }, targetRects[0]);
  } else {
    return targetRects.reduceRight((targetRect, rect) => {
      if (rect.top < targetRect.top || rect.right < cursorRect.left) {
        return targetRect;
      } else {
        return rect;
      }
    }, targetRects[targetRects.length - 1]);
  }
};

const findTargetTextExitingTable = (
  editor: Editor,
  table: NodeEntry<TableElement> | undefined,
  direction: Direction
) => {
  if (!table) return;

  const block = targetBlockOutsideTable(editor, direction, table[1]);
  if (!block) return;

  return direction === 'down'
    ? getFirstTextNode(editor, block[1])
    : getLastTextNode(editor, block[1]);
};

const findTargetRectExitingTable = (
  editor: Editor,
  table: NodeEntry<TableElement> | undefined,
  cursorRect: DOMRect,
  direction: Direction
) => {
  const text = findTargetTextExitingTable(editor, table, direction);

  return findTargetRectInText(editor, text, cursorRect, direction);
};

const handleUpDownArrow = (
  ahaEditor: AhaEditor,
  event: KeyboardEvent
): boolean => {
  const { editor } = ahaEditor;
  if (!editor.selection) return false;

  const direction = event.key === 'ArrowUp' ? 'up' : 'down';
  const isShift = event.shiftKey;
  let targetPath: Path | null = null;

  // Find the next/prev text node in the document, depending on direction
  const targetText = findTargetText(editor, direction);

  if (!targetText) {
    // If there's no target text node, check if we're currently in a table.
    // If we're not, return false and let the default behavior take over.
    const parentTable = TableUtils.currentTable(editor);
    if (!parentTable) return false;

    // If we are in a table, add a new paragraph at the beginning/end of the
    // document, depending on the direction and let the default behavior
    // move the cursor to that paragraph.
    targetPath = direction === 'up' ? [0] : [editor.children.length];
    Transforms.insertNodes(editor, EditorUtils.emptyParagraph(), {
      at: targetPath,
    });
    return false;
  }

  const targetTable = TableUtils.parentTable(editor, targetText[1]);
  if (!targetTable) return false;

  const domSelection = window.getSelection();
  const cursorRect = domSelection?.getRangeAt(0).getBoundingClientRect();
  const currentCell = TableUtils.currentCell(editor);

  ifCellChanged(editor, currentCell, () => {
    if (!cursorRect) return;

    const currentTable = TableUtils.currentTable(editor);
    const differentTables =
      currentTable && !Path.equals(targetTable[1], currentTable[1]);

    const targetCell = findTargetCell(
      editor,
      targetTable,
      cursorRect,
      direction
    ) as NodeEntry<TableCellElement>;

    let targetRect: DOMRect | undefined;

    if (differentTables && Path.isDescendant(targetTable[1], currentTable[1])) {
      targetRect = findTargetRectInCell(
        editor,
        targetCell,
        cursorRect,
        direction
      );
    } else if (targetCell) {
      targetRect = findTargetRectInCell(
        editor,
        targetCell,
        cursorRect,
        direction
      );
    } else if (currentTable && currentCell) {
      targetRect = findTargetRectExitingTable(
        editor,
        currentTable,
        cursorRect,
        direction
      );
    }

    if (!targetRect) {
      targetPath = direction === 'up' ? [0] : [editor.children.length];
      Transforms.insertNodes(editor, EditorUtils.emptyParagraph(), {
        at: targetPath,
      });
      Transforms.select(editor, targetPath);
      return;
    }

    const targetElement =
      targetCell || targetBlockOutsideTable(editor, direction, targetTable[1]);

    moveCursorToText(editor, targetElement, targetRect, cursorRect, isShift);
  });

  return false;
};

export default handleUpDownArrow;
